import client from "./client";
import { ExtractorName } from "./documentIntelligence";
import { CompanyListItem } from "../types/company";

export interface LlmLabDocument {
  company_id: string;
  company_name?: string | null;
  content_id: string;
  source_type: "press_release" | "presentation";
  title: string;
  category: string;
  published_date?: string | null;
}

export interface LlmLabDocumentsResponse {
  environment: "production";
  documents: LlmLabDocument[];
  analyses: ExtractorName[];
  max_models: number;
  max_documents: number;
  worker_enabled: boolean;
  quality_method: string;
}

export interface LlmLabItem {
  title: string;
  detail?: string;
  extra?: string;
}

export interface LlmLabResult {
  content_id: string;
  document_title: string;
  category: string;
  source_type: string;
  extractor: string;
  model: string;
  provider: string;
  ok: boolean;
  skipped: boolean;
  item_count?: number;
  term_count?: number;
  reference_item_count?: number;
  reference_term_count?: number;
  quality_score: number | null;
  validation_errors?: string[];
  error?: string | null;
  latency_ms?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  cost_usd?: number | null;
  cost_source?: string;
  items?: LlmLabItem[];
  raw_output?: string | null;
  normalized_output?: Array<Record<string, unknown>>;
}

export interface LlmLabModelSummary {
  model: string;
  runs: number;
  scored_runs?: number;
  success_rate: number;
  quality_score: number | null;
  total_cost_usd: number;
  average_latency_ms: number;
  quality_per_dollar?: number | null;
  value_rank: number;
}

export interface LlmLabRun {
  run_id: string;
  status:
    | "queued"
    | "queued_v2"
    | "queued_v3"
    | "running"
    | "running_v2"
    | "running_v3"
    | "cancelling"
    | "cancelling_v2"
    | "cancelling_v3"
    | "completed"
    | "degraded"
    | "failed"
    | "cancelled";
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
  environment?: string;
  models: string[];
  analyses: ExtractorName[];
  documents?: LlmLabDocument[];
  documents_total?: number;
  documents_completed?: number;
  current_document?: string | null;
  persisted?: boolean;
  privacy?: {
    openrouter_region: string;
    zero_data_retention: boolean;
    data_collection: string;
  };
  results?: LlmLabResult[];
  summary?: LlmLabModelSummary[];
  error?: string | null;
}

const BASE = "/api/v1/llm-lab";

export const getLlmLabDocuments =
  async (): Promise<LlmLabDocumentsResponse> => {
    const response = await client.get<LlmLabDocumentsResponse>(
      `${BASE}/documents`,
    );
    return response.data;
  };

export const searchLlmLabCompanies = async (
  query: string,
  limit: number = 20,
): Promise<{
  environment: "production";
  results: CompanyListItem[];
  count: number;
}> => {
  const response = await client.get(`${BASE}/companies`, {
    params: { q: query, limit },
  });
  return response.data;
};

export const listLlmLabCompanyDocuments = async (
  companyId: string,
  daysBack: number,
): Promise<{
  company_id: string;
  company_name?: string | null;
  documents: LlmLabDocument[];
}> => {
  const response = await client.get(
    `${BASE}/companies/${encodeURIComponent(companyId)}/documents`,
    { params: { days_back: daysBack } },
  );
  return response.data;
};

export const startLlmLabRun = async (
  models: string[],
  analyses: ExtractorName[],
  documents?: LlmLabDocument[],
): Promise<LlmLabRun> => {
  const response = await client.post<LlmLabRun>(`${BASE}/runs`, {
    models,
    analyses,
    ...(documents ? { documents } : {}),
  });
  return response.data;
};

export const stopLlmLabRun = async (runId: string): Promise<LlmLabRun> => {
  const response = await client.post<LlmLabRun>(
    `${BASE}/runs/${encodeURIComponent(runId)}/stop`,
  );
  return response.data;
};

export const getLlmLabRun = async (runId: string): Promise<LlmLabRun> => {
  const response = await client.get<LlmLabRun>(
    `${BASE}/runs/${encodeURIComponent(runId)}`,
  );
  return response.data;
};
